import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ChevronRight, Search, Plus, Edit } from "lucide-react";
import * as Collapsible from "@radix-ui/react-collapsible";
import { X, ChevronDown, ChevronUp, DeleteIcon, Trash2Icon, Columns3, ArrowDownIcon, ArrowRightIcon } from "lucide-react";
import moment from "moment";
import { custom, z } from 'zod';
import { Decimal } from 'decimal.js';
import {
  CashSalesFiltersValidation, RiskManagementCashSales, useListRiskCashSales, useListStagedCashSales,
  useProcessStagedRow, useDeleteStagedRow
} from '@/lib/api/risk/cashSales';
import { CommonDestinationTimezone, GenerateLoadingOrError, genFlatOptionList, getUniqueStrings } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { BearDecimalInput, BearIntInput } from '@/lib/form/ui';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useDebounce from '@/section/otc/useDebounce';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/virtualizedTable";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Activity, UpdateCustomerActivityInput, useListCustomerActivities, useListCustomers, useListCustomersTodos, useUpdateActivity } from '@/lib/api/customer';
import { cn } from '@/lib/utils';
import { formatCommodityToUom, formatNumberToDisplay, safeParseDecimal, sortOptionMonths } from '@/lib/api/common';
import { Separator } from '@/components/ui/separator';
import { Commodities, CommodityOptions, Uom } from '@/lib/api/commodity';
import { RiskProfileDetailModal } from '@/components/modals/RiskProfileDetailModal';
import { CropProfileReportModal } from '@/section/risk/GenerateProfileReportModal';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useListTraders } from '@/lib/api/trader';
import { Trader } from '@/lib/api/reports';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ContractType, generateContractTypesWith } from '@/lib/api/generalContract';
import { DatePicker } from '@/components/ui/typeable_date_picker';
import { useToast } from "@/components/ui/use-toast";
import { Label } from '@/components/ui/label';
import { BinaryCropTypeSelection, useListRiskSummary, RiskManagementSummaryResult, useListRiskSummaryFullRows, BinaryCropTypeSelectionOptions } from '@/lib/api/risk/summary';
import { createArrayOfSize } from '@/lib/freightReport/frieghtReportFunctions';
import { ContractTypeOptions as OtcContractTypeOptions, ContractType as OtcContractType } from '@/lib/api/otc';
import { RiskCustomerModal } from '@/components/modals/RiskCustomerModal';
import { Loader } from '@/components/ui/loader';
import { Checkbox } from '@/components/ui/checkbox';
import { useListMinimalUsers, useListUsers } from '@/lib/api/users';
import { CustomerActivitiesSection } from '@/components/modals/CustomerActivitiesSection';

export function RiskManagementSalesNotes() {
  const [traderIds, setTraderIds] = useState<string[]>([]);
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const customersResult = useListCustomers({ minimal: true, customer_type: "end_user" });
  const customersList: { id: string; long_name: string; hasRiskProfile: boolean }[] = (customersResult?.data?.data || []);

  const { customerOptions } = useMemo(() => {
      return {
          customerOptions: customersList?.map(customer => ({ value: customer.id, label: customer.long_name })).sort((a, b) => a.label.localeCompare(b.label)) || [],
      }
  }, [customersList]);

  const isDataLoading = customersResult?.isLoading;
  const hasError = !isDataLoading && (!!customersResult?.error);

  return (
    <div className="flex flex-col gap-4">
    <div className="flex flex-row w-full justify-between">
      <h1 className="text-2xl font-bold">Risk Sales Notes</h1>
            <Combobox
                id="customerFilter"
                dwimOnChange
                virtualizeList
                disabled={hasError}
                options={customerOptions}
                value={customerIds?.[0]}
                onValueChange={(val) => setCustomerIds([val])}
                placeholder={isDataLoading ? "Loading customers..." : hasError ? "Error loading customers" : "Select Customer"}
                className=""
                popoverClassName='min-w-[280px] sm:min-w-[320px] md:min-w-[380px] lg:min-w-[420px]'
            />
    </div>
      <CustomerActivitiesSection
        customerId={customerIds?.length > 0 ? customerIds[0] : undefined}
        traderId={traderIds?.length > 0 ? traderIds[0] : undefined}
        showCustomerNames
        customerListFilter={{ minimal: true, customer_type: "end_user" }}
        activity_type={"sales_notes"}
        assignableUsersFilter={(users) => {
          return users.filter(user => ["jordyn"].some(name => user.name?.toLowerCase().includes(name) || ""));
        }}
      />
    </div>
  );
}
